import { Stack } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import MainTable from 'app/views/components/Table/MainTable'
import { useEffect, useMemo, useState } from 'react'
import { FacilityParams } from 'app/models/facility-params'
import { facilityScreenRowsAsParams } from 'app/models/facility-screen-row'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  getFacilityScreenLoader,
  getFacilityScreenRows,
  isFacilityScreenStale,
} from 'app/state/ducks/facility-screen/selectors'
import { loadFacilityScreen } from 'app/state/ducks/facility-screen/thunks'
import getLockedColumnDefs from './locked-facility-table-columnDefs'
import { getKPIData } from '../facilities-kpi-data'
import {
  GridSortingStorage,
  GridFilterStorage,
  GridPaginationStorage,
  GridColumnVisibilityStorage,
  useGridSorting,
  useGridFilters,
  useGridPagination,
  useGridColumnVisibility,
} from 'app/utils/grid-sorting-storage'

/**
 * The Terminated facilities grid. Its rows are the terminated rows of the
 * Facilities read (`GET /api/facility-management/screen`, ADR_LOS-030): one
 * request, shared with the live Facilities grid, with the deal and admin names
 * and the termination time already on each row. It used to load the locked
 * facilities, the deal lists and the whole entity list to name them.
 */
export default function LockedFacilitiesPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const rows = useSelector(getFacilityScreenRows)
  const screenLoading = useSelector(getFacilityScreenLoader)
  const stale = useSelector(isFacilityScreenStale)
  const facilities = useMemo(
    () => facilityScreenRowsAsParams(rows.filter(row => row.status === 'Terminated')),
    [rows],
  )
  const [filteredFacilities, setFilteredFacilities] =
    useState<FacilityParams[]>(facilities)
  const columnDefs = useMemo(() => getLockedColumnDefs(), [])
  const kpiData = getKPIData(facilities, setFilteredFacilities)

  // Grid preferences hooks
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.lockedFacilities,
  )
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(
    GridFilterStorage.lockedFacilities,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.lockedFacilities,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.lockedFacilities)

  useEffect(() => {
    dispatch(loadFacilityScreen())
  }, [])

  // A write the rows could not be patched for: reload once, while on screen.
  useEffect(() => {
    if (stale && !screenLoading) dispatch(loadFacilityScreen())
  }, [stale, screenLoading])

  useEffect(() => {
    setFilteredFacilities(facilities)
  }, [facilities])

  return (
    <Stack>
      <KPI_Section
        kpiData={kpiData}
        isLoading={screenLoading}
      />
      <MainTable
        tableName="Locked_Facilities Table"
        isLoading={screenLoading}
        withPagination={true}
        csvExportEnabled={true}
        enableTopToolbar={false}
        headerBackgroundColor="#F0EEEE"
        columnDefs={columnDefs}
        data={filteredFacilities}
        initialSorting={[
          { id: sorting.sortBy, desc: sorting.order === 'desc' },
        ]}
        onSortingChange={handleSortingChange}
        initialColumnFilters={toMRTColumnFilters()}
        onColumnFiltersChange={handleMRTFiltersChange}
        initialPagination={pagination}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: FacilityParams, cellName: string) => {
          if (cellName != 'id') {
            navigate('/dealmanagement/facilities/' + e.id + '/' + e.status)
          }
        }}
      />
    </Stack>
  )
}
